import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PUSH_KINDS, PUSH_LABELS, type PushPrefs, type PushSettingsResponse } from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { currentSubscription, deviceLabel, isIos, pushSupport, subscribe, unsubscribe } from "../../lib/push";
import { useToast } from "../../lib/toast";

const pushKey = ["push"];
const fmt = (iso: string) => new Date(iso).toLocaleDateString("nl-NL", { dateStyle: "medium" });
const errText = (e: unknown, fallback: string) =>
  e instanceof ApiError || e instanceof Error ? e.message || fallback : fallback;

/**
 * Profiel › Pushmeldingen (issue #223): aanzetten op dit apparaat, soorten kiezen, testmelding en andere
 * apparaten. Verborgen als push op de server uit staat.
 */
export function PushSettings() {
  const toast = useToast();
  const qc = useQueryClient();
  const support = pushSupport();
  /** Endpoint van dit apparaat (null = niet geabonneerd), undefined = nog aan het kijken. */
  const [endpoint, setEndpoint] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void currentSubscription()
      .then((s) => live && setEndpoint(s?.endpoint ?? null))
      .catch(() => live && setEndpoint(null));
    return () => {
      live = false;
    };
  }, []);

  const q = useQuery({
    queryKey: [...pushKey, endpoint ?? ""],
    queryFn: () => api<PushSettingsResponse>(`/push${endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : ""}`),
    enabled: endpoint !== undefined,
  });
  const data = q.data;
  const here = data?.devices.find((d) => d.current);

  // Abonnement in de browser, maar de server kent het (niet meer) voor deze gebruiker: opnieuw melden.
  useEffect(() => {
    if (!data?.enabled || !endpoint || here) return;
    void currentSubscription().then(async (s) => {
      if (!s) return;
      const res = await api<PushSettingsResponse>("/push/subscriptions", {
        method: "POST",
        body: { ...s.toJSON(), label: deviceLabel() },
      }).catch(() => null);
      if (res) qc.setQueryData([...pushKey, endpoint], res);
    });
  }, [data?.enabled, endpoint, here, qc]);

  const set = (res: PushSettingsResponse, ep: string | null = endpoint ?? null) => {
    qc.setQueryData([...pushKey, ep ?? ""], res);
  };

  const prefs = useMutation({
    mutationFn: (p: PushPrefs) => api<PushSettingsResponse>("/push/prefs", { method: "PUT", body: p }),
    onSuccess: (res) => {
      set({ ...res, devices: data?.devices ?? res.devices });
      toast("Opgeslagen");
    },
    onError: (e) => toast(errText(e, "Opslaan mislukt")),
  });

  async function turnOn() {
    if (!data?.publicKey) return;
    setBusy(true);
    try {
      const s = await subscribe(data.publicKey);
      const res = await api<PushSettingsResponse>("/push/subscriptions", {
        method: "POST",
        body: { ...s.toJSON(), label: deviceLabel() },
      });
      setEndpoint(s.endpoint);
      set(res, s.endpoint);
      toast("Pushmeldingen staan aan op dit apparaat");
    } catch (e) {
      toast(errText(e, "Aanzetten mislukt"));
    } finally {
      setBusy(false);
    }
  }

  async function turnOff() {
    setBusy(true);
    try {
      const ep = await unsubscribe();
      if (ep) {
        const res = await api<PushSettingsResponse>("/push/subscriptions/remove", {
          method: "POST",
          body: { endpoint: ep },
        });
        set(res, null);
      }
      setEndpoint(null);
      toast("Pushmeldingen staan uit op dit apparaat");
    } catch (e) {
      toast(errText(e, "Uitzetten mislukt"));
    } finally {
      setBusy(false);
    }
  }

  const test = useMutation({
    mutationFn: () => api<{ ok: number; devices: number }>("/push/test", { method: "POST" }),
    onSuccess: (r) =>
      toast(
        r.ok
          ? `Testmelding verstuurd naar ${r.ok} ${r.ok === 1 ? "apparaat" : "apparaten"}`
          : "Geen apparaat bereikt. Zet meldingen opnieuw aan.",
      ),
    onError: (e) => toast(errText(e, "Versturen mislukt")),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<PushSettingsResponse>(`/push/subscriptions/${id}`, { method: "DELETE" }),
    onSuccess: (res) => set({ ...res, devices: res.devices.map((d) => ({ ...d, current: d.id === here?.id })) }),
    onError: (e) => toast(errText(e, "Verwijderen mislukt")),
  });

  if (!data?.enabled) return null;
  const others = data.devices.filter((d) => !d.current);
  const denied = support === "ok" && typeof Notification !== "undefined" && Notification.permission === "denied";
  const on = Boolean(endpoint && here);

  return (
    <section className="panel" aria-labelledby="p-push" aria-busy={busy}>
      <h2 id="p-push">Pushmeldingen</h2>
      <p className="muted small">
        Een melding op je telefoon of computer, ook als Klantpunt niet open staat. Per apparaat aan te zetten.
      </p>

      {support === "ios-homescreen" ? (
        <div className="note" role="status">
          Op iPhone en iPad werken pushmeldingen alleen als Klantpunt op je beginscherm staat. Tik in Safari op{" "}
          <strong>Deel</strong> en kies <strong>Zet op beginscherm</strong>. Open Klantpunt daarna vanaf het beginscherm
          en zet hier de meldingen aan.
        </div>
      ) : support === "unsupported" ? (
        <div className="note" role="status">
          Deze browser kan geen pushmeldingen ontvangen.
          {isIos()
            ? " Werk je iPhone of iPad bij naar iOS 16.4 of nieuwer."
            : " Probeer Chrome, Edge, Firefox of Safari."}
        </div>
      ) : denied ? (
        <div className="note" role="status">
          Meldingen zijn geblokkeerd voor Klantpunt op dit apparaat. Sta ze toe in de instellingen van je browser (of op
          iPhone: Instellingen › Meldingen › Klantpunt) en kom dan terug.
        </div>
      ) : (
        <div className="profile-row">
          <span className="grow">
            <strong>{on ? "Aan op dit apparaat" : "Uit op dit apparaat"}</strong>
            <br />
            <span className="muted small">{here?.label ?? deviceLabel()}</span>
          </span>
          {on ? (
            <span className="btnrow">
              <button type="button" className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
                Testmelding
              </button>
              <button type="button" className="btn ghost" disabled={busy} onClick={() => void turnOff()}>
                Uitzetten
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn primary"
              disabled={busy || endpoint === undefined}
              onClick={() => void turnOn()}
            >
              Aanzetten
            </button>
          )}
        </div>
      )}

      <fieldset className="checklist" disabled={prefs.isPending}>
        <legend className="label">Waarover</legend>
        {PUSH_KINDS.map((k) => (
          <label key={k}>
            <input
              type="checkbox"
              checked={data.prefs[k]}
              onChange={(e) => prefs.mutate({ ...data.prefs, [k]: e.target.checked })}
            />
            <span>
              {PUSH_LABELS[k].l}
              <br />
              <span className="muted small">{PUSH_LABELS[k].t}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {others.length > 0 && (
        <>
          <p className="label">Andere apparaten</p>
          <ul className="list compact">
            {others.map((d) => (
              <li key={d.id}>
                <span className="grow">
                  {d.label ?? "Apparaat"}
                  <br />
                  <span className="muted small">Sinds {fmt(d.createdAt)}</span>
                </span>
                <button
                  type="button"
                  className="btn small ghost"
                  aria-label={`${d.label ?? "Apparaat"} verwijderen`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(d.id)}
                >
                  Verwijderen
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
